import { useState, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { useManageSpaceMember } from '../../hooks/queries/useSpaceQueries';
import { ISpace, ISpaceMember } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

interface IManageAdminsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  space: ISpace;
}

interface IAdminEntry {
  userId: string;
  displayName: string;
  username: string;
  avatar?: string | null;
}

const AVATAR_COLOR = '#3b82f6';

const ManageAdminsDialog = ({
  isOpen,
  onClose,
  space,
}: IManageAdminsDialogProps) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<ISpaceMember[]>([]);
  const [admins, setAdmins] = useState<IAdminEntry[]>([]);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const manageAdmins = useManageSpaceMember();

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setLoading(true);

    // Load space members and admins in parallel
    Promise.all([
      api.get<ISpaceMember[]>(`/backoffice/spaces/${space.id}/members`),
      api.get<
        {
          id: string;
          username: string;
          displayName: string;
          avatar?: string | null;
        }[]
      >(`/backoffice/spaces/${space.id}/admins`),
    ])
      .then(([memberResult, adminResult]) => {
        setMembers(memberResult);
        setAdmins(
          adminResult.map((a) => ({
            userId: a.id,
            displayName: a.displayName || a.username,
            username: a.username,
            avatar: a.avatar,
          })),
        );
      })
      .catch(() => {
        setMembers([]);
        setAdmins([]);
      })
      .finally(() => setLoading(false));

    setAutocompleteKey(0);
  }, [isOpen, space.id]);

  const adminUserIds = admins.map((a) => a.userId);

  const handleAddAdmin = (userId: string) => {
    if (!userId || adminUserIds.includes(userId)) return;

    const member = members.find((m) => m.userId === userId);

    if (member) {
      setAdmins((prev) => [
        ...prev,
        {
          userId: member.userId,
          displayName:
            member.user?.displayName || member.user?.username || member.userId,
          username: member.user?.username || member.userId,
          avatar: member.user?.avatar,
        },
      ]);
    }

    setAutocompleteKey((prev) => prev + 1);
  };

  const handleRemoveAdmin = (userId: string) => {
    setAdmins((prev) => prev.filter((a) => a.userId !== userId));
  };

  const fetchSpaceMembers = useCallback(
    async (search?: string) => {
      const currentAdminIds = admins.map((a) => a.userId);
      const ownerId = space.owner?.id || space.ownerId;
      const eligible = members.filter(
        (m) => m.userId !== ownerId && !currentAdminIds.includes(m.userId),
      );

      const filtered = search
        ? eligible.filter(
            (m) =>
              m.user?.displayName
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              m.user?.username?.toLowerCase().includes(search.toLowerCase()),
          )
        : eligible;

      return filtered.map((m) => ({
        id: m.userId,
        username: m.user?.username || m.userId,
        displayName: m.user?.displayName || m.user?.username || m.userId,
        avatar: m.user?.avatar,
      }));
    },
    [members, admins, space.owner?.id, space.ownerId],
  );

  const handleSubmit = async () => {
    setError(null);

    try {
      await manageAdmins.mutateAsync({
        spaceId: space.id,
        userIds: adminUserIds,
      });
      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('rooms.space_admin_title')}</DialogTitle>
          <DialogDescription>
            {t('rooms.space_admin_desc', { name: space.name })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Add admin autocomplete */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            {t('rooms.select_admins')}
          </label>
          {loading ? (
            <p className="text-sm text-gray-400 p-3">{t('common.loading')}</p>
          ) : (
            <UserAutocomplete
              key={autocompleteKey}
              value=""
              onChange={handleAddAdmin}
              fetchUsers={fetchSpaceMembers}
              placeholder={t('spaces.search_users')}
              triggerPlaceholder={t('rooms.select_admin_placeholder')}
            />
          )}
        </div>

        {/* Current admins list */}
        <div className="border border-border rounded-lg divide-y divide-border max-h-72 overflow-y-auto">
          {admins.length > 0 ? (
            admins.map((admin) => (
              <div
                key={admin.userId}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                  style={{ backgroundColor: AVATAR_COLOR }}
                >
                  {admin.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {admin.displayName}
                  </div>
                  <div className="text-xs text-gray-500">@{admin.username}</div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveAdmin(admin.userId)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400 p-4 text-center">
              {t('rooms.no_admins')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={manageAdmins.isPending}>
            {manageAdmins.isPending
              ? t('common.saving')
              : t('rooms.confirm_save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageAdminsDialog;
